// src/pages/AdminFeedback.js
//
// Admin-only page for reviewing and actioning submitted feedback.
// Lists all feedback newest-first. Admin can update status and add notes.
//
// Statuses: new | backlog | pending | shipped | ignored

import React, { useEffect, useState, useCallback } from 'react';
import { getFeedback, updateFeedback } from '../services/api';

const STATUS_OPTIONS = ['new', 'backlog', 'pending', 'shipped', 'ignored'];

const STATUS_STYLES = {
    new:     'bg-blue-900/40 text-blue-300 border-blue-700',
    backlog: 'bg-yellow-900/40 text-yellow-300 border-yellow-700',
    pending: 'bg-orange-900/40 text-orange-300 border-orange-700',
    shipped: 'bg-green-900/40 text-green-300 border-green-700',
    ignored: 'bg-gray-800 text-gray-500 border-gray-700',
};

function StatusBadge({ status }) {
    return (
        <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${STATUS_STYLES[status] || STATUS_STYLES.new}`}>
            {status}
        </span>
    );
}

function StarDisplay({ rating }) {
    if (!rating) return <span className="text-gray-600 text-sm">—</span>;
    return (
        <span className="text-orange-400 text-sm">
            {'★'.repeat(rating)}
            <span className="text-gray-700">{'★'.repeat(5 - rating)}</span>
        </span>
    );
}

function FeedbackRow({ item, onUpdate }) {
    const [expanded,   setExpanded]   = useState(false);
    const [status,     setStatus]     = useState(item.status);
    const [notes,      setNotes]      = useState(item.admin_notes || '');
    const [saving,     setSaving]     = useState(false);
    const [savedMsg,   setSavedMsg]   = useState('');

    const handleSave = async () => {
        setSaving(true);
        setSavedMsg('');
        try {
            await updateFeedback(item.id, {
                status,
                admin_notes: notes || null,
            });
            setSavedMsg('Saved');
            onUpdate(item.id, { status, admin_notes: notes || null });
            setTimeout(() => setSavedMsg(''), 2000);
        } catch {
            setSavedMsg('Error saving');
        } finally {
            setSaving(false);
        }
    };

    const date = new Date(item.created_at).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit'
    });

    return (
        <div className={`border rounded-xl mb-3 overflow-hidden transition-all ${
            status === 'ignored' ? 'border-gray-800 opacity-60' : 'border-gray-700'
        }`}>
            {/* Summary row — always visible */}
            <button
                onClick={() => setExpanded(e => !e)}
                className="w-full text-left px-5 py-4 flex items-start gap-4 hover:bg-gray-800/50 transition-colors"
            >
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap mb-1">
                        <span className="text-white font-bold text-sm">{item.participant_name}</span>
                        <span className="text-gray-500 text-xs">{item.participant_email}</span>
                        <StatusBadge status={status} />
                        <StarDisplay rating={item.rating} />
                    </div>
                    <p className="text-gray-300 text-sm truncate">{item.message}</p>
                    <div className="flex gap-3 mt-1 flex-wrap">
                        <span className="text-gray-600 text-xs">{date}</span>
                        {item.route && <span className="text-gray-600 text-xs font-mono">{item.route}</span>}
                        {item.tournament_name && <span className="text-gray-600 text-xs">{item.tournament_name}</span>}
                        {item.pool_name && <span className="text-gray-600 text-xs">{item.pool_name}</span>}
                    </div>
                </div>
                <span className="text-gray-500 text-sm flex-shrink-0 mt-1">
                    {expanded ? '▲' : '▼'}
                </span>
            </button>

            {/* Expanded detail + action panel */}
            {expanded && (
                <div className="px-5 pb-5 border-t border-gray-800 pt-4">
                    {/* Full message */}
                    <div className="bg-gray-800 rounded-lg px-4 py-3 mb-4">
                        <p className="text-gray-300 text-sm whitespace-pre-wrap">{item.message}</p>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4">
                        {/* Status selector */}
                        <div className="flex-1">
                            <label className="block text-gray-500 text-xs uppercase tracking-wide mb-2">
                                Status
                            </label>
                            <select
                                value={status}
                                onChange={e => setStatus(e.target.value)}
                                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500"
                            >
                                {STATUS_OPTIONS.map(s => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Admin notes */}
                    <div className="mt-4">
                        <label className="block text-gray-500 text-xs uppercase tracking-wide mb-2">
                            Admin notes
                        </label>
                        <textarea
                            value={notes}
                            onChange={e => setNotes(e.target.value)}
                            placeholder="Internal notes (not visible to participant)..."
                            rows={3}
                            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500 resize-none"
                        />
                    </div>

                    <div className="flex items-center gap-3 mt-3">
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            className="bg-orange-500 hover:bg-orange-400 disabled:opacity-50 text-white font-bold px-5 py-2 rounded-lg text-sm transition-colors"
                        >
                            {saving ? 'Saving…' : 'Save'}
                        </button>
                        {savedMsg && (
                            <span className={`text-sm font-bold ${savedMsg === 'Saved' ? 'text-green-400' : 'text-red-400'}`}>
                                {savedMsg}
                            </span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function AdminFeedback() {
    const [items,       setItems]       = useState([]);
    const [loading,     setLoading]     = useState(true);
    const [error,       setError]       = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    useEffect(() => {
        getFeedback()
            .then(res => setItems(res.data))
            .catch(() => setError('Failed to load feedback.'))
            .finally(() => setLoading(false));
    }, []);

    const handleUpdate = useCallback((id, changes) => {
        setItems(prev => prev.map(item =>
            item.id === id ? { ...item, ...changes } : item
        ));
    }, []);

    const filtered = statusFilter === 'all'
        ? items
        : items.filter(i => i.status === statusFilter);

    const counts = STATUS_OPTIONS.reduce((acc, s) => {
        acc[s] = items.filter(i => i.status === s).length;
        return acc;
    }, {});

    return (
        <div className="max-w-3xl mx-auto px-4 py-8">
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-black text-white">Feedback</h1>
                <span className="text-gray-500 text-sm">{items.length} total</span>
            </div>

            {/* Status filter pills */}
            <div className="flex gap-2 flex-wrap mb-6">
                <button
                    onClick={() => setStatusFilter('all')}
                    className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors ${
                        statusFilter === 'all'
                            ? 'bg-orange-500 border-orange-400 text-white'
                            : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-white'
                    }`}
                >
                    All ({items.length})
                </button>
                {STATUS_OPTIONS.map(s => (
                    <button
                        key={s}
                        onClick={() => setStatusFilter(s)}
                        className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors ${
                            statusFilter === s
                                ? 'bg-orange-500 border-orange-400 text-white'
                                : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-white'
                        }`}
                    >
                        {s} ({counts[s] || 0})
                    </button>
                ))}
            </div>

            {loading && <p className="text-gray-500 text-sm">Loading…</p>}
            {error   && <p className="text-red-400 text-sm">{error}</p>}

            {!loading && !error && filtered.length === 0 && (
                <p className="text-gray-500 text-sm">No feedback{statusFilter !== 'all' ? ` with status "${statusFilter}"` : ''} yet.</p>
            )}

            {filtered.map(item => (
                <FeedbackRow key={item.id} item={item} onUpdate={handleUpdate} />
            ))}
        </div>
    );
}

export default AdminFeedback;
